import { formatMonthYear } from '@packages/format';
import { CASH_FLOW_ACTIVITY_ORDER } from './ledger.js';
import { seriesAmountsForKey } from './series-presentation.js';
export function cashFlowSeriesColumns(series) {
    return series.periods.map((statement) => ({
        label: formatMonthYear(statement.periodStart.slice(0, 10)),
        periodStart: statement.periodStart,
        periodEnd: statement.periodEnd,
        currency: statement.currency,
    }));
}
function lineKey(line) {
    return line.ledgerAccountId ?? `synthetic:${line.name}`;
}
/**
 * Collapse one block's per-column lines into one row per account, ordered by
 * code then name. A line that appears in any column appears in every column.
 */
export function mergeSeriesLines(linesByColumn) {
    const linesByKey = new Map();
    const amountsByColumn = linesByColumn.map((lines) => {
        const amounts = new Map();
        for (const line of lines) {
            const key = lineKey(line);
            if (!linesByKey.has(key)) {
                linesByKey.set(key, line);
            }
            amounts.set(key, line.amountMinorUnits);
        }
        return amounts;
    });
    return [...linesByKey.entries()]
        .sort((a, b) => {
        const codeA = a[1].code ?? '';
        const codeB = b[1].code ?? '';
        const byCode = codeA.localeCompare(codeB);
        return byCode === 0 ? a[1].name.localeCompare(b[1].name) : byCode;
    })
        .map(([key, line]) => ({
        ledgerAccountId: line.ledgerAccountId,
        code: line.code,
        name: line.name,
        archivedAt: line.archivedAt ?? null,
        amountsMinorUnits: seriesAmountsForKey(amountsByColumn, key),
    }));
}
/**
 * The three closing rows both methods end on. Literal totals, not sums: they
 * come from the statement's own reconciliation, which the backend has already
 * proved balances (§7.20).
 */
export function cashFlowSeriesReconciliationRows(statements) {
    return [
        {
            kind: 'total',
            label: 'Opening cash',
            amountsMinorUnits: statements.map((statement) => statement.openingCashMinorUnits),
            reconciliationRole: 'openingCash',
        },
        {
            kind: 'total',
            label: 'Net change in cash',
            amountsMinorUnits: statements.map((statement) => statement.netChangeInCashMinorUnits),
            reconciliationRole: 'netChangeInCash',
        },
        {
            kind: 'total',
            label: 'Closing cash',
            amountsMinorUnits: statements.map((statement) => statement.closingCashMinorUnits),
            strong: true,
            reconciliationRole: 'closingCash',
        },
    ];
}
/** Emit a heading, its merged lines, and the subtotal that sums them. */
export function cashFlowSeriesGroupRows(label, linesByColumn, subtotalMinorUnits) {
    const lines = mergeSeriesLines(linesByColumn);
    return [
        { kind: 'section', label },
        ...lines.map((line) => ({ kind: 'line', line })),
        {
            kind: 'subtotal',
            label: `Total ${label.toLowerCase()}`,
            amountsMinorUnits: subtotalMinorUnits,
            sumSpan: lines.length,
        },
    ];
}
export function buildCashFlowSeriesTableRows(series) {
    const statements = series.periods;
    const rows = [];
    for (const activity of CASH_FLOW_ACTIVITY_ORDER) {
        const groups = statements.map((statement) => {
            const group = statement.activities.find((candidate) => candidate.activity === activity);
            if (!group) {
                // Should be unreachable: `projectCashFlowActivities` (the
                // direct-method helper `getCashFlow` and `getCashFlowSeries`
                // both build every column through) always returns
                // `CASH_FLOW_ACTIVITY_ORDER.map(...)` unconditionally, filling
                // in a zero-subtotal, empty-lines group for an activity with no
                // movements rather than omitting it. Reaching this throw means
                // a new producer broke that guarantee, not that a user sent
                // malformed input. `packages/types` cannot import
                // `@packages/errors` to raise a structured `internal()` here,
                // so a raw `Error` - loud, not silently coerced with `!` or
                // `??` - is the loudest failure mode this package can offer.
                throw new Error(`Cash flow statement is missing ${activity} activities`);
            }
            return group;
        });
        const firstGroup = groups[0];
        if (!firstGroup)
            continue;
        rows.push(...cashFlowSeriesGroupRows(firstGroup.label, groups.map((group) => group.lines), groups.map((group) => group.subtotalMinorUnits)));
    }
    rows.push(...cashFlowSeriesReconciliationRows(statements));
    return rows;
}
/**
 * A4 landscape column-fit geometry for the cash-flow series PDF: a code
 * column, a name column, and one money column per period.
 *
 * `usableLandscapeWidthPt` is a baked-in figure, not derived from the page
 * margin `reportPdfStyles.page` renders with (`REPORT_PDF_HORIZONTAL_MARGIN_PT`,
 * this same package) - unlike the as-at series' `AS_OF_SERIES_PDF_FIT`, whose
 * usable width is computed from that margin at read time. A change to the page
 * margin does not move this number, so update it by hand if the margin
 * changes, or the maximum-column verdict here will drift from the page it
 * actually renders on.
 */
export const CASH_FLOW_SERIES_PDF_FIT = {
    codeColumnWidthPt: 44,
    minNameColumnWidthPt: 150,
    amountColumnWidthPt: 74,
    usableLandscapeWidthPt: 742,
};
export function maxCashFlowSeriesPdfColumns() {
    const remaining = CASH_FLOW_SERIES_PDF_FIT.usableLandscapeWidthPt -
        CASH_FLOW_SERIES_PDF_FIT.codeColumnWidthPt -
        CASH_FLOW_SERIES_PDF_FIT.minNameColumnWidthPt;
    return Math.max(1, Math.floor(remaining / CASH_FLOW_SERIES_PDF_FIT.amountColumnWidthPt));
}
export function cashFlowSeriesPdfFits(columnCount) {
    return columnCount <= maxCashFlowSeriesPdfColumns();
}
